import React, { useCallback, useMemo } from 'react';
import clsx from 'clsx';
import { useKernel } from '@/kernel/Kernel';
import { useKernelStore } from '@/kernel/kernelStores';
import { Trash2 } from '@/components/ui/lucide-memo';
import { EntitySkeleton, EntityView } from '../EntityView';
import type { AuditResolutionAction } from '#types/types';
import {
  resolveAuditActionAvailability,
  resolvePropertyIdFromAudit,
  resolvePropertyNameFromAudit,
} from '#lib/leasing/auditActions';

interface AuditPageProps {
  auditId: string;
  className?: string;
  hasEntityRowAbove?: boolean;
}

const TARGET_PAGE_SUPPORTED_TYPES = new Set<string>([
  'Brand',
  'Property',
  'Unit',
  'Floor',
  'Deal',
  'Lead',
  'Portfolio',
  'PortfolioFit',
  'LegalEntity',
  'MarketSignal',
  'Project',
  'Organisation',
  'SourcingCampaign',
  'PortfolioFitCampaign',
]);

export const AuditPage = ({ auditId, className }: AuditPageProps) => {
  const { kernel } = useKernel();
  const lang = useKernelStore((state) => state.language);
  const audit = useKernelStore((state) => state.inventory.Audit.registry[auditId] ?? null);

  const copy = useMemo(
    () =>
      lang === 'fr'
        ? {
            review: 'Review',
            target: 'Entite cible',
            issues: 'Problemes detectes',
            suggestedFix: 'Correctif suggere',
            currentValue: 'Valeur actuelle',
            verify: 'Verifier',
            applyFix: 'Appliquer fix',
            webSearch: 'Web search',
            createBrandAndUnit: 'Creer brand + unit',
            notRealBrand: 'Pas une vraie marque',
            clearData: 'Vider donnees',
            terminate: 'Marquer liquidee',
            confirmTerminate: 'Marquer cette marque comme liquidee (sans suppression) et fermer les audits lies ?',
            confirmNotRealBrand: 'Marquer ceci comme une fausse marque et fermer les audits lies ?',
            noIssues: 'Aucun detail',
            notAvailable: 'Non disponible',
            unsupportedTarget: 'La page detaillee de cette entite cible nest pas supportee.',
            noTarget: 'Entite cible introuvable.',
            confirmClearData: 'Vider les donnees suspectes pour cette entite ?',
            score: 'Score',
            procedure: 'Procedure',
            createdAt: 'Cree le',
          }
        : {
            review: 'Review',
            target: 'Target entity',
            issues: 'Flagged issues',
            suggestedFix: 'Suggested fix',
            currentValue: 'Current value',
            verify: 'Verify',
            applyFix: 'Apply fix',
            webSearch: 'Web search',
            createBrandAndUnit: 'Create brand + unit',
            notRealBrand: 'Not a real brand',
            clearData: 'Clear data',
            terminate: 'Mark liquidated',
            confirmTerminate: 'Mark this brand as liquidated (no hard delete) and close linked audits?',
            confirmNotRealBrand: 'Mark this as not a real brand and close linked audits?',
            noIssues: 'No issue details',
            notAvailable: 'Not available',
            unsupportedTarget: 'Detailed page for this target entity type is not supported.',
            noTarget: 'Target entity is missing.',
            confirmClearData: 'Clear suspicious field data for this entity?',
            score: 'Score',
            procedure: 'Procedure',
            createdAt: 'Created',
          },
    [lang]
  );

  const handleResolve = useCallback(
    async (action: AuditResolutionAction) => {
      await kernel.current?.resolveAudit(auditId, action);
    },
    [auditId, kernel]
  );

  const handleVerify = useCallback(() => {
    void handleResolve('VERIFY');
  }, [handleResolve]);

  const handleWebSearch = useCallback(() => {
    if (!audit) return;
    const brandName = (audit.entityName || audit.entityId || '').trim();
    const propertyName = resolvePropertyNameFromAudit(audit) || resolvePropertyIdFromAudit(audit) || '';
    const query = [brandName, propertyName].filter(Boolean).join(' ').trim();
    if (!query) return;
    window.open(`https://www.google.com/search?q=${encodeURIComponent(query)}`, '_blank', 'noopener,noreferrer');
  }, [audit]);

  const handleCreateBrandAndUnit = useCallback(() => {
    if (!audit) return;
    const propertyId = resolvePropertyIdFromAudit(audit);
    void kernel.current?.resolveAudit(auditId, 'CREATE_BRAND_AND_UNIT', propertyId ? { propertyId } : undefined);
  }, [audit, auditId, kernel]);

  const handleNotRealBrand = useCallback(() => {
    if (!window.confirm(copy.confirmNotRealBrand)) return;
    void handleResolve('NOT_REAL_BRAND');
  }, [copy, handleResolve]);

  const handleApplyFix = useCallback(() => {
    void handleResolve('FIX');
  }, [handleResolve]);

  const handleClearData = useCallback(() => {
    if (!window.confirm(copy.confirmClearData)) return;
    void handleResolve('CLEAR_DATA');
  }, [copy, handleResolve]);

  const handleTerminate = useCallback(() => {
    if (!window.confirm(copy.confirmTerminate)) return;
    void handleResolve('TERMINATE_ENTITY');
  }, [copy, handleResolve]);

  if (!audit) {
    return <EntitySkeleton variant="page" className={className} />;
  }

  const issues = Array.isArray(audit.flaggedIssues) ? audit.flaggedIssues : [];
  const { canResolve, isPropertyBrandPipelineReview, hasSuggestedFix, canApplyFix, canClearData, canTerminate } =
    resolveAuditActionAvailability(audit);
  const canRenderTargetPage =
    typeof audit.entityType === 'string' &&
    typeof audit.entityId === 'string' &&
    audit.entityId.length > 0 &&
    TARGET_PAGE_SUPPORTED_TYPES.has(audit.entityType);

  const createdAt = (() => {
    if (!audit.createdAt) return '-';
    const parsed = new Date(audit.createdAt);
    return Number.isNaN(parsed.getTime()) ? '-' : parsed.toLocaleDateString();
  })();

  return (
    <div className={clsx('flex min-h-0 flex-col bg-[#141414] text-white', className)}>
      <div className="grid min-h-0 flex-1 gap-3 p-3 lg:grid-cols-[440px_minmax(0,1fr)]">
        <div className="min-h-0 overflow-y-auto rounded-xl border border-[#2A2A2A] bg-[#181818] p-4">
          <div className="mb-3 text-[11px] text-white/45">{copy.review}</div>
          <div className="mb-3 grid grid-cols-3 gap-2 text-[11px]">
            <div className="rounded border border-[#2A2A2A] bg-[#151515] px-2 py-2">
              <div className="text-white/40">{copy.score}</div>
              <div className="font-mono text-white/90">{audit.plausibilityScore}</div>
            </div>
            <div className="rounded border border-[#2A2A2A] bg-[#151515] px-2 py-2">
              <div className="text-white/40">{copy.procedure}</div>
              <div className="truncate text-white/90" title={audit.procedureName}>{audit.procedureName}</div>
            </div>
            <div className="rounded border border-[#2A2A2A] bg-[#151515] px-2 py-2">
              <div className="text-white/40">{copy.createdAt}</div>
              <div className="text-white/90">{createdAt}</div>
            </div>
          </div>

          <div className="mb-3 flex flex-wrap items-center gap-2">
            {canResolve && (isPropertyBrandPipelineReview ? (
              <button
                type="button"
                onClick={handleVerify}
                title="Close audit"
                aria-label="Close audit"
                className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-white/15 bg-white/[0.06] text-white/75 transition-colors hover:bg-white/10 hover:text-white"
              >
                <Trash2 size={14} />
              </button>
            ) : (
              <button
                type="button"
                onClick={handleVerify}
                className="h-8 rounded-md border border-white/15 bg-white/[0.06] px-3 text-[11px] text-white/75 transition-colors hover:bg-white/10 hover:text-white"
              >
                {copy.verify}
              </button>
            ))}
            {isPropertyBrandPipelineReview && (
              <button
                type="button"
                onClick={handleWebSearch}
                className="h-8 rounded-md border border-sky-500/35 bg-sky-500/10 px-3 text-[11px] text-sky-200 transition-colors hover:bg-sky-500/15"
              >
                {copy.webSearch}
              </button>
            )}
            {isPropertyBrandPipelineReview && (
              <button
                type="button"
                onClick={handleCreateBrandAndUnit}
                className="h-8 rounded-md border border-emerald-500/35 bg-emerald-500/10 px-3 text-[11px] text-emerald-200 transition-colors hover:bg-emerald-500/15"
              >
                {copy.createBrandAndUnit}
              </button>
            )}
            {isPropertyBrandPipelineReview && (
              <button
                type="button"
                onClick={handleNotRealBrand}
                className="h-8 rounded-md border border-rose-500/35 bg-rose-500/10 px-3 text-[11px] text-rose-200 transition-colors hover:bg-rose-500/15"
              >
                {copy.notRealBrand}
              </button>
            )}
            {canApplyFix && (
              <button
                type="button"
                onClick={handleApplyFix}
                className="h-8 rounded-md border border-emerald-500/35 bg-emerald-500/10 px-3 text-[11px] text-emerald-200 transition-colors hover:bg-emerald-500/15"
              >
                {copy.applyFix}
              </button>
            )}
            {canClearData && (
              <button
                type="button"
                onClick={handleClearData}
                className="h-8 rounded-md border border-amber-500/35 bg-amber-500/10 px-3 text-[11px] text-amber-200 transition-colors hover:bg-amber-500/15"
              >
                {copy.clearData}
              </button>
            )}
            {canTerminate && (
              <button
                type="button"
                onClick={handleTerminate}
                className="h-8 rounded-md border border-rose-500/35 bg-rose-500/10 px-3 text-[11px] text-rose-200 transition-colors hover:bg-rose-500/15"
              >
                {copy.terminate}
              </button>
            )}
          </div>

          <div className="space-y-3 text-[12px]">
            <div className="rounded border border-[#2A2A2A] bg-[#151515] p-3">
              <div className="mb-2 text-white/55">{copy.issues}</div>
              {issues.length > 0 ? (
                <ul className="list-disc space-y-1 pl-4 text-white/80">
                  {issues.map((issue, index) => (
                    <li key={`${audit.id}-issue-${index}`}>{issue}</li>
                  ))}
                </ul>
              ) : (
                <div className="italic text-white/35">{copy.noIssues}</div>
              )}
            </div>

            <div className="rounded border border-[#2A2A2A] bg-[#151515] p-3">
              <div className="mb-2 text-white/55">{copy.suggestedFix}</div>
              <pre className="max-h-[180px] overflow-auto whitespace-pre-wrap break-all text-[11px] leading-relaxed text-white/75">
                {hasSuggestedFix ? JSON.stringify(audit.suggestedFix, null, 2) : copy.notAvailable}
              </pre>
            </div>

            <div className="rounded border border-[#2A2A2A] bg-[#151515] p-3">
              <div className="mb-2 text-white/55">{copy.currentValue}</div>
              <pre className="max-h-[180px] overflow-auto whitespace-pre-wrap break-all text-[11px] leading-relaxed text-white/75">
                {audit.currentValue !== undefined && audit.currentValue !== null
                  ? JSON.stringify(audit.currentValue, null, 2)
                  : copy.notAvailable}
              </pre>
            </div>
          </div>
        </div>

        <div className="min-h-0 overflow-hidden rounded-xl border border-[#2A2A2A] bg-[#181818]">
          <div className="border-b border-[#2A2A2A] px-4 py-3 text-[11px] text-white/45">
            {copy.target}: {audit.entityType} / {audit.entityId}
          </div>
          <div className="min-h-0 h-[70vh] overflow-y-auto p-3">
            {canRenderTargetPage ? (
              <EntityView type={audit.entityType} entityId={audit.entityId} variant="page" />
            ) : typeof audit.entityId === 'string' && audit.entityId.length > 0 ? (
              <div className="rounded border border-[#2A2A2A] bg-[#151515] p-4 text-[12px] text-white/60">
                {copy.unsupportedTarget}
              </div>
            ) : (
              <div className="rounded border border-[#2A2A2A] bg-[#151515] p-4 text-[12px] text-white/60">
                {copy.noTarget}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
